import React, { useState } from 'react';
import { Box, Button, Chip, Collapse, IconButton, Tooltip, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import HistoryIcon from '@material-ui/icons/History';
import {
  audienceSummary,
  availabilityColour,
  gateNotices,
  releaseColour,
} from './flagDisplay';

const badgeStyle = ({ bg, fg }) => ({
  backgroundColor: bg,
  color: fg,
  fontWeight: 500,
  height: 22,
});

/**
 * One node of the flag tree, rendered recursively.
 *
 * CONTAINER nodes are shown, not hidden. They are auto-created from key prefixes and are
 * usually inert, but they are also the lever that takes a whole area down at once — so
 * hiding them would hide the fastest way to respond to an incident.
 */
const FlagTreeNode = ({ node, depth = 0, onEditRelease, onEditAvailability, onViewAudit }) => {
  const [open, setOpen] = useState(true);
  const row = (node.node && node.node.row) || {};
  const meta = node.node && node.node.meta;
  const hasChildren = (node.children || []).length > 0;

  const isContainer = row.kind === 'CONTAINER';
  const availColour = availabilityColour(row.availability);
  const notices = gateNotices(node);

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        style={{
          paddingLeft: 8 + depth * 24,
          paddingRight: 8,
          paddingTop: 10,
          paddingBottom: 10,
          borderBottom: '1px solid #eceff1',
          backgroundColor: isContainer ? '#fafafa' : 'transparent',
        }}>
        <Box width={32}>
          {hasChildren && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)}>
              {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>

        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              style={{ fontFamily: 'monospace', fontWeight: isContainer ? 400 : 500 }}>
              {row.key}
            </Typography>

            {isContainer && (
              <Tooltip title="Auto-created from a key prefix. Use it to take everything beneath it down at once.">
                <Chip size="small" label="container" variant="outlined" style={{ height: 22 }} />
              </Tooltip>
            )}

            <Chip
              size="small"
              label={row.releaseStatus}
              style={badgeStyle(releaseColour(row.releaseStatus))}
            />

            {availColour && (
              <Tooltip title={row.availabilityReason || ''}>
                <Chip
                  size="small"
                  label={row.availability}
                  style={badgeStyle(availColour)}
                />
              </Tooltip>
            )}

            {notices.map((notice) => (
              <Tooltip
                key={notice.text}
                title={
                  notice.severity === 'blocked'
                    ? 'An ancestor is off or not live, so nobody sees this whatever its own setting says.'
                    : 'An ancestor is on a pilot or canary, so this reaches at most that ancestor’s audience.'
                }>
                <Chip
                  size="small"
                  variant="outlined"
                  label={notice.text}
                  style={{
                    height: 22,
                    borderColor: notice.severity === 'blocked' ? '#b3261e' : '#8a6100',
                    color: notice.severity === 'blocked' ? '#b3261e' : '#8a6100',
                  }}
                />
              </Tooltip>
            ))}
          </Box>

          <Typography variant="caption" color="textSecondary">
            {audienceSummary(row)}
            {meta && meta.owner ? ` · owner ${meta.owner}` : ''}
            {meta && meta.removeBy ? ` · remove by ${meta.removeBy}` : ''}
            {meta && meta.description ? ` · ${meta.description}` : ''}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Button size="small" onClick={() => onEditRelease(node)}>
            Release
          </Button>
          {/* Visually separated from Release: killing is an incident action, not a
              rollout step, and the API keeps them apart for the same reason. */}
          <Button
            size="small"
            style={{ color: row.availability === 'LIVE' ? '#b3261e' : '#1e7b34' }}
            onClick={() => onEditAvailability(node)}>
            {row.availability === 'LIVE' ? 'Kill / Maintain' : 'Restore'}
          </Button>
          <Tooltip title="Release log">
            <IconButton size="small" onClick={() => onViewAudit(node)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {node.children.map((child) => (
            <FlagTreeNode
              key={child.key}
              node={child}
              depth={depth + 1}
              onEditRelease={onEditRelease}
              onEditAvailability={onEditAvailability}
              onViewAudit={onViewAudit}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

export default FlagTreeNode;
